import React, { useEffect, useRef, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import siteAnalyticsService, { AudienceResponse } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, fmtDuration } from './useDateRange';
import { COLORS, defaultDoughnutOptions, PALETTE } from './chartConfig';
import './analytics.css';

const DEVICE_FA: Record<string, string> = {
  desktop: 'دسکتاپ',
  mobile: 'موبایل',
  tablet: 'تبلت',
  bot: 'ربات',
  unknown: 'نامشخص',
};

const SiteAnalyticsAudience: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<AudienceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getAudience(dr.range);
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const deviceData = data
    ? {
        labels: data.byDevice.map((r) => DEVICE_FA[r.value] || r.value || 'نامشخص'),
        datasets: [{ data: data.byDevice.map((r) => r.sessions), backgroundColor: COLORS }],
      }
    : null;

  const countryBars = data
    ? {
        labels: data.byCountry.slice(0, 12).map((r) => r.value || '(نامشخص)'),
        datasets: [
          {
            label: 'سشن',
            data: data.byCountry.slice(0, 12).map((r) => r.sessions),
            backgroundColor: PALETTE.primary,
          },
        ],
      }
    : null;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>مخاطبان</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          <div className="analytics-kpi-grid">
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">سشن‌های لاگین‌کرده</div>
              <div className="value">{fa(data.authStats.authenticated)}</div>
              <span className="change flat">
                {data.authStats.authenticated + data.authStats.anonymous > 0
                  ? `${fa(Math.round((data.authStats.authenticated / (data.authStats.authenticated + data.authStats.anonymous)) * 100))}٪ از کل`
                  : ''}
              </span>
            </div>
            <div className="analytics-kpi-card purple">
              <div className="accent" />
              <div className="label">سشن‌های مهمان</div>
              <div className="value">{fa(data.authStats.anonymous)}</div>
            </div>
            {/* 🆕 جدید vs بازگشتی */}
            {data.newVsReturning && (
              <>
                <div className="analytics-kpi-card green">
                  <div className="accent" />
                  <div className="label">بازدیدکنندهٔ جدید</div>
                  <div className="value">{fa(data.newVsReturning.newVisitors)}</div>
                  <span className="change flat">
                    {data.newVsReturning.newVisitors + data.newVsReturning.returningVisitors > 0
                      ? `${fa(Math.round((data.newVsReturning.newVisitors / (data.newVsReturning.newVisitors + data.newVsReturning.returningVisitors)) * 100))}٪ از کل`
                      : ''}
                  </span>
                </div>
                <div className="analytics-kpi-card teal">
                  <div className="accent" />
                  <div className="label">بازدیدکنندهٔ بازگشتی</div>
                  <div className="value">{fa(data.newVsReturning.returningVisitors)}</div>
                </div>
              </>
            )}
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>توزیع دستگاه</h3>
              </div>
              <div className="analytics-chart-container">
                {deviceData && deviceData.datasets[0].data.length > 0 ? (
                  <Doughnut data={deviceData} options={defaultDoughnutOptions} />
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>کشور (Top 12)</h3>
              </div>
              <div className="analytics-chart-container">
                {countryBars && countryBars.datasets[0].data.length > 0 ? (
                  <Bar
                    data={countryBars}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      indexAxis: 'y' as const,
                      plugins: { legend: { display: false } },
                      scales: { x: { beginAtZero: true } },
                    }}
                  />
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>مرورگرها</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>مرورگر</th>
                    <th>سشن</th>
                    <th>کاربر</th>
                    <th>Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byBrowser.slice(0, 12).map((r) => (
                    <tr key={r.value}>
                      <td>{r.value || '(نامشخص)'}</td>
                      <td>{fa(r.sessions)}</td>
                      <td>{fa(r.visitors)}</td>
                      <td>{fa(r.bounceRate)}٪</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>سیستم‌عامل‌ها</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>OS</th>
                    <th>سشن</th>
                    <th>کاربر</th>
                    <th>Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byOS.slice(0, 12).map((r) => (
                    <tr key={r.value}>
                      <td>{r.value || '(نامشخص)'}</td>
                      <td>{fa(r.sessions)}</td>
                      <td>{fa(r.visitors)}</td>
                      <td>{fa(r.bounceRate)}٪</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>کشورها (Top 30)</h3>
            </div>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>کشور</th>
                  <th>سشن</th>
                  <th>کاربر</th>
                  <th>بازدید صفحه</th>
                  <th>Bounce</th>
                  <th>میانگین مدت</th>
                </tr>
              </thead>
              <tbody>
                {data.byCountry.map((r) => (
                  <tr key={r.value || ''}>
                    <td>{r.value || '(نامشخص)'}</td>
                    <td>{fa(r.sessions)}</td>
                    <td>{fa(r.visitors)}</td>
                    <td>{fa(r.pageViews)}</td>
                    <td>{fa(r.bounceRate)}٪</td>
                    <td>{fmtDuration(r.avgDuration)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data.byCity && data.byCity.length > 0 && (
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>شهرها (Top 20)</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>شهر</th>
                    <th>سشن</th>
                    <th>کاربر</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCity.map((r) => (
                    <tr key={r.value || ''}>
                      <td>{r.value || '(نامشخص)'}</td>
                      <td>{fa(r.sessions)}</td>
                      <td>{fa(r.visitors)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>زبان مرورگر</h3>
            </div>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>زبان</th>
                  <th>سشن</th>
                </tr>
              </thead>
              <tbody>
                {data.byLanguage.map((r) => (
                  <tr key={r.value || ''}>
                    <td>{r.value || '(نامشخص)'}</td>
                    <td>{fa(r.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsAudience;
